import { Info, PlusIcon, TrashIcon } from '@phosphor-icons/react'
import ReactTooltip from 'react-tooltip'
import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { toastFactory } from '@/factories'

import ToggleField from '../../../../FormLegacy/FormFieldsV2/ToggleField'
import DateField from '../../../../FormLegacy/FormFieldsV2/DateField'
import TextField from '../../../../FormLegacy/FormFieldsV2/TextField'
import { onChangeNumberField } from '../helpers'

const TABS = ['active', 'upcoming', 'past']

const SpecialPricingSection = ({ slots, specialPricing, handleChange, setFieldValue, error }) => {
  const { t } = useTranslation(['reservations'])
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [price, setPrice] = useState('')
  const [activeTab, setActiveTab] = useState('active')

  const slotsToDisplay = slots.filter((slot) => !slot.repeats && !slot.startTime)
  const excludedDates = slotsToDisplay.map((slot) => new Date(slot.startDate))
  const isAddDisabled = !startDate || !price

  const tabLabels = {
    active: t('reservations:amenityForms.specialPricing.tabs.active'),
    past: t('reservations:amenityForms.specialPricing.tabs.past'),
    upcoming: t('reservations:amenityForms.specialPricing.tabs.upcoming'),
  }

  const specialPricingItems = useMemo(() => {
    const startOfToday = new Date()
    startOfToday.setHours(0, 0, 0, 0)

    const endOfToday = new Date()
    endOfToday.setHours(23, 59, 59, 999)

    const result = {
      active: [],
      upcoming: [],
      past: [],
    }

    for (const event of slotsToDisplay) {
      if (event.endDate < startOfToday.getTime()) {
        result.past.push(event)
      } else if (event.startDate > endOfToday.getTime()) {
        result.upcoming.push(event)
      } else {
        result.active.push(event)
      }
    }

    result.upcoming.sort((a, b) => a.startDate - b.startDate)
    result.active.sort((a, b) => a.startDate - b.startDate)
    result.past.sort((a, b) => b.endDate - a.endDate)

    return result
  }, [slotsToDisplay])

  const resetFields = () => {
    setStartDate('')
    setEndDate('')
    setPrice('')
  }

  const handleAddSpecialPriceItem = () => {
    const overlaps = slotsToDisplay.some((item) => {
      return startDate <= item.endDate && endDate >= item.startDate
    })

    if (overlaps) {
      toastFactory.error(t('reservations:amenityForms.specialPricing.overlapError'))
      return
    }

    setFieldValue('specialPricingSlots', [
      ...slots,
      { startDate, endDate, price: parseFloat(price), repeats: false, weekdays: [] },
    ])
    resetFields()
  }

  const handleRemoveSpecialPriceItem = (itemToRemove) => {
    const foundIndex = slots.findIndex(
      (item) =>
        item.startDate === itemToRemove.startDate &&
        item.endDate === itemToRemove.endDate &&
        item.price === itemToRemove.price,
    )

    if (foundIndex === -1) return

    const updated = [...slots]
    updated.splice(foundIndex, 1)

    setFieldValue('specialPricingSlots', updated)
  }

  useEffect(() => {
    // Keep the user's current tab selected as long as it still has items.
    if (specialPricingItems[activeTab].length > 0) {
      return
    }

    // If the current tab became empty (e.g. after adding/removing items),
    // automatically switch to the first tab that still has content.
    const firstAvailableTab = TABS.find((tab) => specialPricingItems[tab].length > 0)

    if (firstAvailableTab) {
      setActiveTab(firstAvailableTab)
    }
  }, [specialPricingItems, activeTab])

  return (
    <>
      <div className="form-step__row form-step__row--between">
        <div>
          <div className="form-step__label">
            {t('reservations:multiReservationAmenityForm.specialPricing')}
            <Info size={16} data-tip data-for="specialPricingTooltip" />
            <ReactTooltip
              className="tooltip"
              multiline={true}
              id="specialPricingTooltip"
              place="top"
              effect="solid"
            >
              {t('reservations:amenityForms.pricing.specialPricingTooltip')}
            </ReactTooltip>
          </div>
        </div>
        <div>
          <ToggleField
            name="specialPricing"
            label={
              specialPricing
                ? t('reservations:amenityForms.yes')
                : t('reservations:amenityForms.no')
            }
            value={!!specialPricing}
            onChange={handleChange}
            labelOnRight
          />
        </div>
      </div>
      {specialPricing && (
        <div className="amenity-special-pricing">
          <div className="special-pricing-selector__form">
            <div className="form-step__column">
              <DateField
                name="specialPricingStartDate"
                label={t('reservations:amenityForms.specialPricing.startDateLabel')}
                placeholderText={t('reservations:shared.selectDatePlaceholder')}
                value={startDate}
                onChange={(e) => {
                  setEndDate('')

                  const date = new Date(e.target.value)
                  date.setHours(0, 0, 0, 0)
                  setStartDate(date.getTime())
                }}
                minDate={new Date()}
                excludeDates={excludedDates}
              />
            </div>
            <div className="form-step__column">
              <DateField
                name="specialPricingEndDate"
                label={t('reservations:amenityForms.specialPricing.endDateLabel')}
                placeholderText={t('reservations:shared.selectDatePlaceholder')}
                value={endDate}
                onChange={(e) => {
                  const date = new Date(e.target.value)
                  date.setHours(23, 59, 59, 999)
                  setEndDate(date.getTime())
                }}
                minDate={startDate ? new Date(startDate) : new Date()}
                excludeDates={excludedDates}
              />
            </div>
            <div className="form-step__column">
              <TextField
                name="specialPricingPrice"
                label={t('reservations:multiReservationAmenityForm.price')}
                placeholder={t('reservations:amenityForms.pricingSelectors.enterPricePlaceholder')}
                value={price}
                onChange={(e) => onChangeNumberField(e, (e) => setPrice(e.target.value))}
                type="number"
                min="0"
              />
            </div>
            <div className="special-pricing-selector__add-button">
              <button
                type="button"
                onClick={handleAddSpecialPriceItem}
                disabled={isAddDisabled}
                className="button button-black"
              >
                {t('reservations:amenityForms.specialPricing.addButton')}
                <PlusIcon size={20} />
              </button>
            </div>
          </div>

          <div className="form-step__divider" />

          <div className="tabs-container">
            {TABS.map((tab) => {
              const tabName = tabLabels[tab]
              const numberOfItems = specialPricingItems[tab].length

              if (!numberOfItems) {
                return null
              }

              return (
                <div
                  key={tab}
                  className={`tab tab--${tab} ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tabName}
                  {numberOfItems ? (
                    <div className={`counter counter--${tab}`}>{numberOfItems}</div>
                  ) : null}
                </div>
              )
            })}
          </div>
          <div className="special-prices-list">
            {slotsToDisplay.length ? (
              specialPricingItems[activeTab].map((specialPriceItem, index) => {
                return (
                  <div
                    className="special-prices-list__item"
                    key={`${index}-${specialPriceItem.startDate}`}
                  >
                    <DateField
                      label={t('reservations:amenityForms.specialPricing.startDateLabel')}
                      value={specialPriceItem.startDate}
                      disabled
                    />
                    <DateField
                      label={t('reservations:amenityForms.specialPricing.endDateLabel')}
                      value={specialPriceItem.endDate}
                      disabled
                    />
                    <TextField
                      label={t('reservations:multiReservationAmenityForm.price')}
                      value={specialPriceItem.price}
                      disabled
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveSpecialPriceItem(specialPriceItem)}
                      className="button button-white"
                    >
                      <TrashIcon size={20} style={{ color: 'red' }} />
                    </button>
                  </div>
                )
              })
            ) : (
              <div className="special-prices-list--empty-list">
                <img
                  src="/images/percentage-start.svg"
                  alt={t('reservations:amenityForms.specialPricing.emptyState.imageAlt')}
                />
                <h2>{t('reservations:amenityForms.specialPricing.emptyState.title')}</h2>
                <p>{t('reservations:amenityForms.specialPricing.emptyState.line1')}</p>
                <p>{t('reservations:amenityForms.specialPricing.emptyState.line2')}</p>
              </div>
            )}
          </div>
          {error && <div className="form-step__error">{error}</div>}
        </div>
      )}
    </>
  )
}

export default SpecialPricingSection
